/** @prettier */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { PRODUCT_LINKS } from '@xanadu/ui-shared/constants';
import ChildrenPropsContext from '@xanadu/ui-shared/contexts/ChildrenPropsContext';
import { useImprovedTranslation } from '@xanadu/ui-shared/hooks';
import { toggleNavigation as toggleNavigationAct } from '@xanadu/ui-shared/modules/navigation/actions';
import { loginRequest as loginRequestAct } from '@xanadu/ui-shared/modules/userSession/actions';

import { triggerUpdateSidebar } from '../../modules/sidebar/actions';

import IconAccount from '../../../../images/icons/misc/account.svg';
import IconMenu from '../../../../images/icons/misc/burger.svg';
import IconHome from '../../../../images/icons/misc/icon-home.svg';
import IconMultiples from '../../../../images/icons/misc/icon-multiples.svg';
import IconPortfolio from '../../../../images/icons/misc/icon-portfolio.svg';
import IconPromos from '../../../../images/icons/misc/icon-promotions.svg';
import IconSearch from '../../../../images/icons/misc/search.svg';

import style from './MobileFooter.module.scss';

const MobileFooter = ({
  history: { push: navigate },
  location: { pathname },
  navigationVisible,
  toggleNavigation,
  sidebarVisible,
  sidebarContent,
  updateSidebar,
  product,
  isLoggedIn,
  loginRequest,
}) => {
  const { t } = useImprovedTranslation();
  const prevRef = useRef();
  const [selectedItem, setSelectedItem] = useState('home');

  const { customization: { MobileFooter: { items = [] } = {} } = {} } =
    useContext(ChildrenPropsContext) || {};

  const itemIds = useMemo(() => items.map(({ id }) => id), [items]);

  const icons = useMemo(
    () => ({
      home: IconHome,
      search: IconSearch,
      betslip: IconPortfolio,
      portfolio: IconPortfolio,
      account: IconAccount,
      promotions: IconPromos,
      menu: IconMenu,
      multiples: IconMultiples,
    }),
    []
  );

  const getCompleteClass = (key, gtm) =>
    classNames({
      [style.footerItem]: true,
      [style.selected]: selectedItem === key,
      '.x-ut-hook-selected': selectedItem === key, // for unit tests only
      '.gtm_cta': gtm,
    });

  const handleClick = (clickedItem) => () => {
    if (clickedItem === 'home') {
      updateSidebar({
        visible: false,
      });

      const productRedirect = PRODUCT_LINKS.find(({ id }) => id === product);

      if (productRedirect) {
        navigate(productRedirect.url);
      } else {
        navigate('/');
      }
    }

    if (clickedItem === 'promotions') {
      toggleNavigation(!navigationVisible);

      updateSidebar({
        visible: false,
      });

      if (selectedItem === clickedItem) {
        navigate('/');
      } else {
        navigate('/promotions');
      }
    }

    if (clickedItem === 'account') {
      toggleNavigation(!navigationVisible);

      updateSidebar({
        visible: false,
      });

      if (selectedItem === clickedItem) {
        navigate('/');
      } else if (isLoggedIn) {
        navigate('/account');
      } else {
        loginRequest({ redirectOnClose: '/account' });
      }
    }

    if (clickedItem === 'portfolio') {
      toggleNavigation(!navigationVisible);

      updateSidebar({
        visible: false,
      });

      if (selectedItem === clickedItem) {
        navigate('/');
      } else if (isLoggedIn) {
        navigate('/portfolio');
      } else {
        loginRequest({ redirectOnClose: '/portfolio' });
      }
    }

    if (clickedItem === 'menu') {
      toggleNavigation(!navigationVisible);
    } else {
      toggleNavigation(false);
    }

    if (['multiples', 'betslip', 'search'].includes(clickedItem)) {
      updateSidebar({
        content: clickedItem,
        visible: sidebarContent !== clickedItem || !sidebarVisible,
      });
    }

    setSelectedItem(selectedItem === clickedItem ? 'home' : clickedItem);
    prevRef.current = clickedItem;
  };

  useEffect(() => {
    if (sidebarVisible) {
      setSelectedItem(sidebarContent);
    }

    if (!sidebarVisible && !navigationVisible) {
      const promotionsAvailable = itemIds.includes('promotions');
      const accountAvailable = itemIds.includes('account');
      const portfolioAvailable = itemIds.includes('portfolio');

      if (promotionsAvailable && pathname.startsWith('/promotions')) {
        setSelectedItem('promotions');
      } else if (accountAvailable && pathname.startsWith('/account') && isLoggedIn) {
        setSelectedItem('account');
      } else if (portfolioAvailable && pathname.startsWith('/portfolio') && isLoggedIn) {
        setSelectedItem('portfolio');
      } else {
        setSelectedItem('home');
      }
    }
  }, [sidebarContent, sidebarVisible, pathname, navigationVisible]);

  // TO REMOVE before launch
  useEffect(() => {
    try {
      document.querySelector('#ot-sdk-btn-floating').style.display = 'none';
      document.querySelector('#launcher').style.display = 'none';
    } catch (e) {
      //
    }
  }, []);

  return (
    <div className={style.mobileFooter}>
      {items.map(({ id, label }) => {
        const IconComponent = icons[id];
        const dataHook = id === 'menu' ? 'mob-menu-nav-btn' : `mob-footer-${id}`;
        return (
          <div
            role="button"
            key={id}
            className={getCompleteClass(id, id !== 'menu')}
            onClick={handleClick(id)}
            data-hook={dataHook}
          >
            {Boolean(IconComponent) && <IconComponent className={style.icon} />}
            <span className={style.label}>{t(label)}</span>
          </div>
        );
      })}
    </div>
  );
};

MobileFooter.propTypes = {
  product: PropTypes.string,
  sidebarContent: PropTypes.string,
  sidebarVisible: PropTypes.bool,
  navigationVisible: PropTypes.bool,
  toggleNavigation: PropTypes.func.isRequired,
  updateSidebar: PropTypes.func.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  isLoggedIn: PropTypes.bool.isRequired,
  loginRequest: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  updateSidebar: triggerUpdateSidebar,
  toggleNavigation: toggleNavigationAct,
  loginRequest: loginRequestAct,
};

function mapStateToProps(state) {
  return {
    navigationVisible: state.getIn(['navigation', 'navigationVisible']),
    sidebarContent: state.getIn(['local', 'sidebar', 'content']),
    sidebarVisible: state.getIn(['local', 'sidebar', 'visible'], ''),
    product: state.getIn(['application', 'product']),
    isLoggedIn: state.getIn(['user', 'isLoggedIn'], false),
  };
}

export const Component = MobileFooter;

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MobileFooter));
